// src/components/Modals/CreateEditUserModal.jsx
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { styled } from '@mui/material/styles';
import {
  Dialog, AppBar, Toolbar, IconButton, Typography, Button, Box,
  Container, Card, CardContent, Slide, Stack, CircularProgress,
  FormControl, FormHelperText, MenuItem, Select, TextField,
  Snackbar, Alert,
} from '@mui/material';
import CloseIcon        from '@mui/icons-material/Close';
import PersonAddIcon    from '@mui/icons-material/PersonAdd';
import EditIcon         from '@mui/icons-material/Edit';
import LockIcon         from '@mui/icons-material/Lock';
import BusinessIcon     from '@mui/icons-material/Business';
import GroupIcon        from '@mui/icons-material/Group';
import CheckCircleIcon  from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

import { useAuth } from '../../contexts/AuthContext';
import { authService } from '../../services/authService';
import { userService } from '../../services/userService';
import { companyService } from '../../services/companyService';
import { departmentService } from '../../services/departmentService';
import { locationService } from '../../services/locationService';
import { normalizeRole, getAssignableRoles, ROLES } from '../../utils/roles';
import { singleValueListService } from '../../services/singleValueListService';

/* ── Slide Up Transition ── */
const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const EMPTY_FORM = {
  name:            '',
  email:           '',
  password:        '',
  confirmPassword: '',
  companyId:       '',
  departmentId:    '',
  locationCode:    '',
  role:            ROLES.USER,
  description:     '',
};

/* ── Styled Components ── */
const FormRow = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
  gap: '1rem',
  marginBottom: '1rem',
  '&:last-child': { marginBottom: 0 },
  [theme.breakpoints.down('sm')]: { gridTemplateColumns: '1fr' },
}));

const FormGroup = styled(Box, {
  shouldForwardProp: (p) => p !== 'hasError' && p !== 'layoutType',
})(({ theme, hasError, layoutType }) => ({
  gridColumn:
    layoutType === 'full'    ? '1 / -1' :
    layoutType === 'compact' ? 'span 1' : 'span 2',
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: '0.375rem',
  '& .MuiOutlinedInput-root': {
    background: theme.palette.background.paper,
    '& fieldset': {
      borderColor: hasError ? theme.palette.error.main : theme.palette.divider,
    },
    '&:hover fieldset': {
      borderColor: hasError ? theme.palette.error.main : theme.palette.primary.main,
    },
    '&.Mui-focused fieldset': {
      borderColor: hasError ? theme.palette.error.main : theme.palette.primary.main,
      boxShadow: hasError
        ? `0 0 0 3px ${theme.palette.error.light}22`
        : `0 0 0 3px ${theme.palette.primary.light}22`,
    },
  },
  '& .MuiInputBase-input.Mui-disabled': {
    backgroundColor: theme.palette.action.disabledBackground,
    color: theme.palette.text.disabled,
    cursor: 'not-allowed',
  },
}));

const FieldLabel = styled(Typography)(({ theme }) => ({
  fontSize: '0.8125rem',
  fontWeight: 600,
  color: theme.palette.text.primary,
  textTransform: 'uppercase',
  letterSpacing: '0.025em',
}));

const RequiredMark = styled('span')(({ theme }) => ({
  color: theme.palette.error.main,
  marginLeft: '0.125rem',
}));

const FieldHint = styled(Typography)(({ theme }) => ({
  fontSize: '0.75rem',
  color: theme.palette.text.secondary,
  fontStyle: 'italic',
}));

const ErrorText = styled(FormHelperText)(({ theme }) => ({
  color: theme.palette.error.main,
  fontSize: '0.75rem',
  fontWeight: 500,
  margin: 0,
  marginTop: '4px',
}));

const SectionCard = styled(Card)(({ theme }) => ({
  borderRadius: 8,
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
}));

const SectionTitle = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
  fontSize: '0.9375rem',
  fontWeight: 700,
  color: theme.palette.text.primary,
  marginBottom: '1.25rem',
  paddingBottom: '0.75rem',
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

/* ── Main Component ── */
const CreateEditUserModal = ({
  open,
  user = null,
  onClose,
  onSuccess,
  onError,
  currentUserRole,
}) => {
  const { currentUser: loggedInUser } = useAuth();
  const isEditMode = useMemo(() => Boolean(user && user.id), [user]);

  const [formData,            setFormData]            = useState(EMPTY_FORM);
  const [companies,           setCompanies]           = useState([]);
  const [departments,         setDepartments]         = useState([]);
  const [locations,           setLocations]           = useState([]);
  const [localSubmitting,     setLocalSubmitting]     = useState(false);
  const [error,               setError]               = useState('');
  const [errors,              setErrors]              = useState({});
  const [successMessage,      setSuccessMessage]      = useState('');
  const [userRole,            setUserRole]            = useState(null);
  const [fetchingCompanies,   setFetchingCompanies]   = useState(false);
  const [fetchingDepartments, setFetchingDepartments] = useState(false);
  const [fetchingLocations,   setFetchingLocations]   = useState(false);
  const [userDataLoaded,      setUserDataLoaded]      = useState(false);
const [catalogRoles,        setCatalogRoles]        = useState([]);
const [rolesLoading,        setRolesLoading]        = useState(false);

  const formBodyRef = useRef(null);
  const timeoutRef  = useRef(null);

  const scrollToField = useCallback((fieldName) => {
    const el = document.getElementById(fieldName) || document.querySelector(`[name="${fieldName}"]`);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setTimeout(() => {
      try {
        const focusable = el.querySelector('input, textarea, [tabindex]') || el;
        focusable.focus();
      } catch { /* noop */ }
    }, 450);
  }, []);

  useEffect(() => () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
  }, []);

  // Sync/reset on open
  useEffect(() => {
    if (!open) return;
    if (isEditMode && user) {
      setFormData({
        name:            user.name         || user.displayName || '',
        email:           user.email        || '',
        password:        '',
        confirmPassword: '',
        companyId:       user.companyId    || '',
        departmentId:    user.departmentId || '',
        locationCode:    user.locationCode || '',
        role:            user.role         || ROLES.USER,
        description:     user.description  || '',
      });
    } else {
      setFormData(EMPTY_FORM);
    }
    setError('');
    setErrors({});
    setSuccessMessage('');
  }, [open, user, isEditMode]);

  // Auth / role-based prefills
  useEffect(() => {
    if (!open || !loggedInUser) return;

    const resolvedRole = normalizeRole(currentUserRole || loggedInUser.role);
    setUserRole(resolvedRole);

    if (!isEditMode) {
      if (resolvedRole === ROLES.ADMIN) {
        setFormData((prev) => ({
          ...prev,
          companyId: loggedInUser.companyId || '',
        }));
      } else if (resolvedRole === ROLES.LOCATION_ADMIN) {
        setFormData((prev) => ({
          ...prev,
          companyId:    loggedInUser.companyId    || '',
          locationCode: loggedInUser.locationCode || '',
        }));
      }
    }
    setUserDataLoaded(true);
  }, [open, loggedInUser, currentUserRole, isEditMode]);
  // Load roles catalog from DB
  useEffect(() => {
    if (!open || !loggedInUser?.companyId) return;
    let cancelled = false;
    setRolesLoading(true);

    (async () => {
      try {
        const items = await singleValueListService.getItems("user_roles", {
          companyId: loggedInUser.companyId,
        });
        if (cancelled) return;
        setCatalogRoles(items || []);
      } catch (err) {
        console.warn("User roles load failed:", err);
        if (!cancelled) setCatalogRoles([]);
      } finally {
        if (!cancelled) setRolesLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [open, loggedInUser?.companyId]);
  // Load companies
  useEffect(() => {
    if (!userRole || !userDataLoaded || !open || !loggedInUser) return;

    const load = async () => {
      setFetchingCompanies(true);
      try {
        if (userRole === ROLES.SUPERADMIN) {
         const list = await companyService.getAllCompanies(loggedInUser);
          setCompanies(list);
        } else {
          setCompanies([{
            id: loggedInUser.companyId || 'default',
            companyId: loggedInUser.companyId || '',
            name: loggedInUser.company || '',
          }]);
        }
      } catch (err) {
        console.error('Failed to load companies:', err);
        setCompanies([]);
      } finally {
        setFetchingCompanies(false);
      }
    };

    load();
  }, [userRole, userDataLoaded, open, loggedInUser]);

  // Load departments (scoped dynamically to selected company)
  useEffect(() => {
    if (!userRole || !userDataLoaded || !open || !loggedInUser) return;

    const load = async () => {
      setFetchingDepartments(true);
      try {
        const companyId = userRole === ROLES.SUPERADMIN ? formData.companyId : loggedInUser.companyId;
        const list = await departmentService.fetchDepartments({ companyId });
        setDepartments(list || []);
      } catch (err) {
        console.error('Failed to load departments:', err);
        setDepartments([]);
      } finally {
        setFetchingDepartments(false);
      }
    };

    load();
  }, [userRole, userDataLoaded, open, loggedInUser, formData.companyId]);

  // Load locations (scoped dynamically to selected company)
  useEffect(() => {
    if (!userRole || !userDataLoaded || !open || !loggedInUser) return;

    const load = async () => {
      setFetchingLocations(true);
      try {
        const companyId = userRole === ROLES.SUPERADMIN ? formData.companyId : loggedInUser.companyId;
        const list = await locationService.fetchLocations({ companyId });
        setLocations(list || []);
      } catch (err) {
        console.error('Failed to load locations:', err);
        setLocations([]);
      } finally {
        setFetchingLocations(false);
      }
    };

    load();
  }, [userRole, userDataLoaded, open, loggedInUser, formData.companyId]);

  /* ── Dynamic filtering ── */
  const filteredDepartments = useMemo(() => {
    if (userRole === ROLES.SUPERADMIN && !formData.companyId) {
      return departments;
    }
    if (!formData.companyId) return [];
    return departments.filter(
      (d) =>
        !d.companyId || String(d.companyId) === String(formData.companyId),
    );
  }, [departments, formData.companyId, userRole]);

  const filteredLocations = useMemo(() => {
    if (userRole === ROLES.SUPERADMIN && !formData.companyId) {
      return locations;
    }
    if (!formData.companyId) return [];
    return locations.filter(
      (l) =>
        !l.companyId || String(l.companyId) === String(formData.companyId),
    );
  }, [locations, formData.companyId, userRole]);

  /* ── Name resolvers (FK code → display name) ── */
  const resolveCompanyName = useCallback((companyId) => {
    if (!companyId) return '';
    const match = companies.find(
      (c) => String(c.companyId || c.id) === String(companyId)
    );
    return match ? (match.name || '') : '';
  }, [companies]);

  const resolveDepartmentName = useCallback((departmentId) => {
    if (!departmentId) return '';
    const match = departments.find((d) => String(d.id) === String(departmentId));
    return match ? (match.departmentName || match.name || '') : '';
  }, [departments]);

  const resolveLocationName = useCallback((locationCode) => {
    if (!locationCode) return '';
    const match =
      locations.find((l) => String(l.locationCode || l.id) === String(locationCode)) ||
      filteredLocations.find((l) => String(l.locationCode || l.id) === String(locationCode));
    return match ? (match.locationName || match.name || '') : '';
  }, [locations, filteredLocations]);

  // Derived display values for the form fields (edit mode / locked fields)
  const companyDisplayName    = resolveCompanyName(formData.companyId);
  const departmentDisplayName = resolveDepartmentName(formData.departmentId);
  const locationDisplayName   = resolveLocationName(formData.locationCode);

  /* ── Handlers ── */
  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setError('');

    if (name === 'companyId') {
      setFormData((p) => ({
        ...p,
        companyId:    value,
        departmentId: '',
        locationCode: '',
      }));
    } else if (name === 'departmentId') {
      setFormData((p) => ({ ...p, departmentId: value }));
    } else if (name === 'locationCode') {
      setFormData((p) => ({ ...p, locationCode: value }));
    } else {
      setFormData((p) => ({ ...p, [name]: value }));
    }

    setErrors((prev) => (prev[name] ? { ...prev, [name]: '' } : prev));
  }, []);

  const passwordMatch = useMemo(() => {
    if (isEditMode) return null;
    if (!formData.password || !formData.confirmPassword) return null;
    return formData.password === formData.confirmPassword;
  }, [isEditMode, formData.password, formData.confirmPassword]);

   const availableRoles = useMemo(() => {
    // Fall back to static list while catalog is loading / empty
    if (!catalogRoles || catalogRoles.length === 0) {
      return getAssignableRoles(userRole || currentUserRole);
    }

    // Keep the permission filter from the static roles util
    const assignableStatics = getAssignableRoles(userRole || currentUserRole);
    const assignableValues = new Set(
      assignableStatics.map((r) => String(r.value).toLowerCase()),
    );

    return catalogRoles
      .map((r) => ({
        label: r.label || r.value,
        value: r.value,
      }))
      .filter((r) => assignableValues.has(String(r.value).toLowerCase()));
  }, [catalogRoles, userRole, currentUserRole]);
    // Ensure the selected role exists in the DB catalog; if not, fall
  // back to the first available option.
  useEffect(() => {
    if (!open) return;
    if (rolesLoading) return;
    if (availableRoles.length === 0) return;

    setFormData((prev) => {
      const stillValid = availableRoles.some(
        (r) => String(r.value) === String(prev.role),
      );
      if (stillValid) return prev;
      return { ...prev, role: availableRoles[0].value };
    });
  }, [open, rolesLoading, availableRoles]);

   const showCompanyDropdown = userRole === ROLES.SUPERADMIN;
  const isCompanyLocked     = userRole !== ROLES.SUPERADMIN;
  const isLocationLocked    = userRole === ROLES.LOCATION_ADMIN;

  const isLocationOptional     = userRole === ROLES.SUPERADMIN;
  const isDepartmentOptional   = userRole === ROLES.SUPERADMIN;

  /* ── Validation ── */
  const validate = useCallback(() => {
    const e = {};
    const { name, email, password, confirmPassword, companyId, locationCode, role } = formData;

    if (!name.trim()) e.name = 'Full Name is required';

    if (!isCompanyLocked) {
      if (!companyId || !String(companyId).trim()) e.companyId = 'Company is required';
    }

     // ── Location: required unless the caller is a Superadmin ──
    if (!isLocationOptional) {
      if (fetchingLocations) {
        e.locationCode = 'Locations are still loading — please wait';
      } else if (!locationCode || !String(locationCode).trim()) {
        e.locationCode = 'Location is required';
      }
    }
    if (!role || !role.trim()) e.role = 'Role is required';

    if (!isEditMode) {
      if (!email.trim()) {
        e.email = 'Email is required';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
        e.email = 'Please enter a valid email address';
      }

      if (!password) {
        e.password = 'Password is required';
      } else if (password.length < 6) {
        e.password = 'Password must be at least 6 characters long';
      }

      if (!confirmPassword) {
        e.confirmPassword = 'Please confirm your password';
      } else if (password && confirmPassword !== password) {
        e.confirmPassword = 'Passwords do not match';
      }
    }

    setErrors(e);

    const errorKeys = Object.keys(e);
    if (errorKeys.length > 0) {
      setError('Please fix highlighted errors before submitting.');
      setTimeout(() => scrollToField(errorKeys[0]), 100);
    }

    return errorKeys.length === 0;
    }, [formData, isEditMode, isCompanyLocked, isLocationOptional, fetchingLocations, scrollToField]);
  /* ── Submit ── */
  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!validate()) return;

    setLocalSubmitting(true);
    setError('');
    setSuccessMessage('');

    try {
          const finalPayload = {
        ...formData,
        companyId:    String(formData.companyId || '').trim(),
        departmentId: formData.departmentId ? String(formData.departmentId).trim() : null,
        locationCode: formData.locationCode ? String(formData.locationCode).trim() : null, 
      };

      const payload = isEditMode
        ? (() => {
            const { password, confirmPassword, ...rest } = finalPayload;
            return rest;
          })()
        : finalPayload;

      if (isEditMode) {
        // Enforce the standard upsert mapping structure instead of non-existent updateUserProfile
        await userService.upsertData(user.id, payload, { 
          isCreation: false, 
          createdByRole: userRole 
        });
      } else {
        await authService.createUserWithAuth(payload, loggedInUser);
      }

      const msg = isEditMode ? 'User updated successfully!' : 'User created successfully!';
      setSuccessMessage(msg);

      timeoutRef.current = setTimeout(() => {
        if (onSuccess) onSuccess(msg);
        onClose();
      }, 1000);
    } catch (err) {
      console.error(err);
      let msg = isEditMode ? 'Failed to update user' : 'Failed to create user';
      switch (err.code) {
        case 'auth/email-already-in-use':   msg = 'Email already registered'; break;
        case 'auth/invalid-email':          msg = 'Invalid email address';    break;
        case 'auth/weak-password':          msg = 'Password too weak';        break;
        case 'auth/network-request-failed': msg = 'Network error';            break;
        default: msg = err.message || msg;
      }
      setError(msg);
      if (formBodyRef.current) {
        formBodyRef.current.scrollTo({ top: 0, behavior: 'smooth' });
      }
      if (onError) onError(msg);
      setLocalSubmitting(false);
    }
  };

  if (!open) return null;

    const isSubmitDisabled =
    localSubmitting ||
    Boolean(successMessage) ||
    fetchingCompanies ||
    fetchingDepartments ||
    fetchingLocations ||
    rolesLoading;

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={!localSubmitting && !successMessage ? onClose : undefined}
      TransitionComponent={Transition}
      PaperProps={{ sx: { display: 'flex', flexDirection: 'column', overflow: 'hidden' } }}
    >
      <AppBar sx={{ position: 'relative', flexShrink: 0 }} elevation={1}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={onClose}
            disabled={localSubmitting || Boolean(successMessage)}
          >
            <CloseIcon />
          </IconButton>
          <Box sx={{ ml: 2, flex: 1 }}>
            <Typography variant="h6" sx={{ lineHeight: 1.2 }}>
              {isEditMode ? 'Edit User' : 'Create New User'}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.85 }}>
              {isEditMode
                ? `Editing: ${formData.name || user?.email || 'Loading…'}`
                : 'Fill in the details below and submit to create the user'}
            </Typography>
          </Box>
        </Toolbar>
      </AppBar>

      <Box sx={{
        display: 'flex', flexDirection: 'column',
        bgcolor: 'background.default', flex: 1, minHeight: 0, overflow: 'hidden',
      }}>
        <Box
          component="form"
          id="user-form"
          ref={formBodyRef}
          onSubmit={handleSubmit}
          noValidate
          sx={{ flex: 1, overflowY: 'auto', py: 4 }}
        >
          <Container maxWidth="lg">
            <Stack spacing={3}>
              {successMessage && (
                <Box sx={{
                  backgroundColor: 'success.light',
                  color: 'success.contrastText',
                  padding: '0.75rem 1rem',
                  borderRadius: '6px',
                  fontSize: '0.875rem',
                  borderLeft: '4px solid',
                  borderColor: 'success.main',
                  display: 'flex', alignItems: 'center', gap: '0.5rem',
                }}>
                  <CheckCircleIcon fontSize="small" />
                  {successMessage}
                </Box>
              )}

              {/* PERSONAL INFO */}
              <SectionCard variant="outlined">
                <CardContent sx={{ p: 3 }}>
                  <SectionTitle>
                    {isEditMode ? <EditIcon color="primary" fontSize="small" /> : <PersonAddIcon color="primary" fontSize="small" />}
                    <Typography sx={{ fontSize: '0.9375rem', fontWeight: 700 }}>
                      Personal Information
                    </Typography>
                  </SectionTitle>

                  <FormRow>
                    <FormGroup layoutType="normal" hasError={Boolean(errors.name)}>
                      <FieldLabel>Full Name <RequiredMark>*</RequiredMark></FieldLabel>
                      <TextField
                        id="name" name="name"
                        value={formData.name} onChange={handleChange}
                        placeholder="Enter full name"
                        fullWidth size="small"
                        disabled={localSubmitting || Boolean(successMessage)}
                        error={Boolean(errors.name)}
                        helperText={errors.name || ''}
                      />
                    </FormGroup>

                    <FormGroup layoutType="normal" hasError={Boolean(errors.email)}>
                      <FieldLabel>Email {!isEditMode && <RequiredMark>*</RequiredMark>}</FieldLabel>
                      <TextField
                        id="email" name="email" type="email"
                        value={formData.email} onChange={handleChange}
                        placeholder="user@example.com"
                        fullWidth size="small"
                        disabled={isEditMode || localSubmitting || Boolean(successMessage)}
                        error={Boolean(errors.email)}
                        helperText={errors.email || ''}
                      />
                      {isEditMode && !errors.email && (
                        <FieldHint>Email cannot be changed after creation</FieldHint>
                      )}
                    </FormGroup>
                  </FormRow>
                </CardContent>
              </SectionCard>

              {/* SECURITY */}
              {!isEditMode && (
                <SectionCard variant="outlined">
                  <CardContent sx={{ p: 3 }}>
                    <SectionTitle>
                      <LockIcon color="primary" fontSize="small" />
                      <Typography sx={{ fontSize: '0.9375rem', fontWeight: 700 }}>
                        Security Credentials
                      </Typography>
                    </SectionTitle>

                    <FormRow>
                      <FormGroup layoutType="normal" hasError={Boolean(errors.password)}>
                        <FieldLabel>Password <RequiredMark>*</RequiredMark></FieldLabel>
                        <TextField
                          id="password" name="password" type="password"
                          value={formData.password} onChange={handleChange}
                          placeholder="Minimum 6 characters"
                          fullWidth size="small"
                          disabled={localSubmitting || Boolean(successMessage)}
                          error={Boolean(errors.password)}
                          helperText={errors.password || ''}
                        />
                      </FormGroup>

                      <FormGroup layoutType="normal" hasError={Boolean(errors.confirmPassword) || passwordMatch === false}>
                        <FieldLabel>Confirm Password <RequiredMark>*</RequiredMark></FieldLabel>
                        <TextField
                          id="confirmPassword" name="confirmPassword" type="password"
                          value={formData.confirmPassword} onChange={handleChange}
                          placeholder="Re-enter password"
                          fullWidth size="small"
                          disabled={localSubmitting || Boolean(successMessage)}
                          error={Boolean(errors.confirmPassword) || passwordMatch === false}
                          helperText={errors.confirmPassword || ''}
                        />
                        {!errors.confirmPassword && formData.confirmPassword && (
                          <Typography sx={{
                            fontSize: '0.75rem',
                            color: passwordMatch ? 'success.main' : 'error.main',
                            fontWeight: 500, mt: 0.5,
                          }}>
                            {passwordMatch ? '✓ Passwords match' : '✗ Passwords do not match'}
                          </Typography>
                        )}
                      </FormGroup>
                    </FormRow>
                  </CardContent>
                </SectionCard>
              )}

              {/* ORGANIZATION */}
              <SectionCard variant="outlined">
                <CardContent sx={{ p: 3 }}>
                  <SectionTitle>
                    <BusinessIcon color="primary" fontSize="small" />
                    <Typography sx={{ fontSize: '0.9375rem', fontWeight: 700 }}>
                      Organization Details
                    </Typography>
                  </SectionTitle>

                  <FormRow>
                    {/* Company */}
                    <FormGroup layoutType="compact" hasError={Boolean(errors.companyId)}>
                      <FieldLabel>Company {!isCompanyLocked && <RequiredMark>*</RequiredMark>}</FieldLabel>
                      {showCompanyDropdown ? (
                        fetchingCompanies ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
                            <CircularProgress size={16} />
                            <Typography variant="body2">Loading companies…</Typography>
                          </Box>
                        ) : (
                          <FormControl fullWidth size="small" error={Boolean(errors.companyId)}>
                            <Select
                              id="companyId" name="companyId"
                              value={formData.companyId} onChange={handleChange}
                              displayEmpty
                              disabled={localSubmitting || Boolean(successMessage) || companies.length === 0}
                              renderValue={(selected) =>
                                selected ? (resolveCompanyName(selected) || selected) : 'Select a company'
                              }
                            >
                              <MenuItem value="">
                                {companies.length === 0 ? 'No companies available' : 'Select a company'}
                              </MenuItem>
                              {companies.map((c) => (
                                <MenuItem key={c.id || c.companyId} value={c.companyId || c.id}>
                                  {c.name}
                                </MenuItem>
                              ))}
                            </Select>
                            {errors.companyId && <ErrorText>{errors.companyId}</ErrorText>}
                          </FormControl>
                        )
                      ) : (
                        <TextField
                          id="companyId" name="companyId"
                          value={companyDisplayName || formData.companyId || ''}
                          fullWidth size="small" disabled
                          placeholder="Company"
                          error={Boolean(errors.companyId)}
                          helperText={errors.companyId || ''}
                        />
                      )}
                    </FormGroup>

                    {/* Company ID (read-only) */}
                    <FormGroup layoutType="compact" hasError={Boolean(errors.companyId)}>
                      <FieldLabel>Company ID {!isCompanyLocked && <RequiredMark>*</RequiredMark>}</FieldLabel>
                      <TextField
                        id="companyIdDisplay" name="companyIdDisplay"
                        value={formData.companyId}
                        fullWidth size="small" disabled
                        placeholder="Auto-filled"
                        error={Boolean(errors.companyId)}
                        helperText={errors.companyId || ''}
                      />
                    </FormGroup>

                    {/* Location */}
                                      <FormGroup layoutType="compact" hasError={Boolean(errors.locationCode)}>
                      <FieldLabel>
                        Location {!isLocationOptional && <RequiredMark>*</RequiredMark>}
                      </FieldLabel>

                      {isLocationLocked ? (
                        <TextField
                          id="locationCode" name="locationCode"
                          value={formData.locationCode
                            ? `(${formData.locationCode}) ${locationDisplayName || ''}`
                            : (locationDisplayName || formData.locationCode || '')}
                          fullWidth size="small" disabled
                          placeholder="Auto-filled"
                        />
                      ) : fetchingLocations ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
                          <CircularProgress size={16} />
                          <Typography variant="body2">Loading locations…</Typography>
                        </Box>
                      ) : (
                        <FormControl fullWidth size="small" error={Boolean(errors.locationCode)}>
                          <Select
                            id="locationCode" name="locationCode"
                            value={formData.locationCode} onChange={handleChange}
                            displayEmpty
                            disabled={localSubmitting || Boolean(successMessage)}
                                                      renderValue={(selected) => {
                              if (!selected) {
                                if (isLocationOptional) return 'Optional — none';
                                return !formData.companyId && userRole === ROLES.SUPERADMIN
                                  ? 'Select a company first (or choose a location)'
                                  : filteredLocations.length === 0
                                    ? 'No locations available'
                                    : 'Select a location';
                              }
                              const name = resolveLocationName(selected);
                              return name ? `(${selected}) ${name}` : selected;
                            }}
                          >
                                                      <MenuItem value="">
                              {isLocationOptional
                                ? 'None (optional)'
                                : !formData.companyId && userRole === ROLES.SUPERADMIN
                                  ? 'Select a company first (or choose a location)'
                                  : filteredLocations.length === 0
                                    ? 'No locations available'
                                    : 'Select a location'}
                            </MenuItem>
                            {filteredLocations.map((loc) => (
                              <MenuItem key={loc.id} value={loc.locationCode || loc.id}>
                                {loc.locationCode ? `(${loc.locationCode}) ` : ''}{loc.locationName || loc.name}
                              </MenuItem>
                            ))}
                          </Select>
                          {errors.locationCode && <ErrorText>{errors.locationCode}</ErrorText>}
                        </FormControl>
                      )}
                    </FormGroup>

                    {/* Department */}
                                      <FormGroup layoutType="compact">
                      <FieldLabel>
                        Department {!isDepartmentOptional && <RequiredMark>*</RequiredMark>}
                      </FieldLabel>
                      {fetchingDepartments ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
                          <CircularProgress size={16} />
                          <Typography variant="body2">Loading departments…</Typography>
                        </Box>
                      ) : (
                        <FormControl fullWidth size="small">
                          <Select
                            id="departmentId" name="departmentId"
                            value={formData.departmentId} onChange={handleChange}
                            displayEmpty
                            disabled={localSubmitting || Boolean(successMessage)}
                            renderValue={(selected) => {
                              if (!selected) {
                                return !formData.companyId && userRole === ROLES.SUPERADMIN
                                  ? 'Select a company first (or choose a department)'
                                  : filteredDepartments.length === 0
                                    ? 'No departments available'
                                    : 'Select a department';
                              }
                              return resolveDepartmentName(selected) || selected;
                            }}
                          >
                            <MenuItem value="">
                              {!formData.companyId && userRole === ROLES.SUPERADMIN
                                ? 'Select a company first (or choose a department)'
                                : filteredDepartments.length === 0
                                  ? 'No departments available'
                                  : 'Select a department'}
                            </MenuItem>
                            {filteredDepartments.map((d) => (
                              <MenuItem key={d.id} value={d.id}>
                                {d.departmentName || d.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                    </FormGroup>
                  </FormRow>
                </CardContent>
              </SectionCard>

              {/* ROLE & DESCRIPTION */}
              <SectionCard variant="outlined">
                <CardContent sx={{ p: 3 }}>
                  <SectionTitle>
                    <GroupIcon color="primary" fontSize="small" />
                    <Typography sx={{ fontSize: '0.9375rem', fontWeight: 700 }}>
                      Role &amp; Additional Info
                    </Typography>
                  </SectionTitle>

                  <FormRow>
                    <FormGroup layoutType="compact" hasError={Boolean(errors.role)}>
                      <FieldLabel>Role <RequiredMark>*</RequiredMark></FieldLabel>
                                         <FormControl fullWidth size="small" error={Boolean(errors.role)}>
                        <Select
                          id="role" name="role"
                          value={formData.role} onChange={handleChange}
                          disabled={localSubmitting || Boolean(successMessage) || rolesLoading}
                        >
                                                   {rolesLoading && (
                            <MenuItem value="" disabled>Loading roles…</MenuItem>
                          )}
                          {!rolesLoading && availableRoles.length === 0 && (
                            <MenuItem value="" disabled>No roles available</MenuItem>
                          )}
                          {!rolesLoading && availableRoles.map((r) => (
                            <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>
                          ))}
                        </Select>
                        {errors.role && <ErrorText>{errors.role}</ErrorText>}
                      </FormControl>
                    </FormGroup>

                    <FormGroup layoutType="full">
                      <FieldLabel>Description</FieldLabel>
                      <TextField
                        id="description" name="description"
                        value={formData.description} onChange={handleChange}
                        placeholder="User description (optional)"
                        multiline rows={3} fullWidth size="small"
                        disabled={localSubmitting || Boolean(successMessage)}
                      />
                    </FormGroup>
                  </FormRow>
                </CardContent>
              </SectionCard>
            </Stack>
          </Container>
        </Box>

        {/* Footer */}
        <Box sx={{
          borderTop: '1px solid', borderColor: 'divider',
          backgroundColor: 'background.paper',
          py: 2, px: 3, flexShrink: 0,
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
            <Button
              type="button" onClick={onClose}
              disabled={localSubmitting || Boolean(successMessage)}
              variant="outlined" color="inherit"
              sx={{ textTransform: 'none', fontWeight: 500, borderRadius: 2 }}
            >
              Cancel
            </Button>
            <Button
              type="submit" form="user-form"
              disabled={isSubmitDisabled}
              variant="contained" color="primary"
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, minWidth: 140 }}
            >
              {localSubmitting ? <CircularProgress size={16} color="inherit" sx={{ mr: 1 }} /> : null}
              {localSubmitting ? 'Saving…' : 'Submit'}
            </Button>
          </Box>
        </Box>
      </Box>

      <Snackbar
        open={Boolean(error)}
        autoHideDuration={6000}
        onClose={() => setError('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ mt: 8 }}
      >
        <Alert
          onClose={() => setError('')}
          severity="error" variant="filled"
          icon={<ErrorOutlineIcon />}
          sx={{ width: '100%', minWidth: 320, borderRadius: 2 }}
        >
          {error}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default CreateEditUserModal;